import { safePrivacyUrl } from './landingConfig';

// Public demo request client. Deliberately plain fetch: no cookies, no Authorization header, no browser storage,
// no HTTP cache, and nothing about the request is logged or sent to analytics.

const ENDPOINT = `${process.env.REACT_APP_BACKEND_URL || ''}/api/public/demo-requests`;
const TIMEOUT_MS = 15000;

export const newIdempotencyKey = () => {
  const bytes = new Uint8Array(16);
  const webCrypto = window.crypto || window.msCrypto;
  if (webCrypto?.getRandomValues) {
    webCrypto.getRandomValues(bytes);
  } else {
    // The key only has to be unique per submission, it is not a secret.
    bytes.forEach((_, i) => { bytes[i] = Math.floor(Math.random() * 256); });
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const request = async (path, options = {}) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    return await fetch(`${ENDPOINT}${path}`, {
      credentials: 'omit',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      signal: controller.signal,
      ...options,
    });
  } finally {
    clearTimeout(timer);
  }
};

// Resolves to { accepting, privacyUrl }. Intake counts as open only when the server says so AND names a usable
// https privacy page; any failure (network, non-200, malformed) means "not accepting".
export const fetchDemoConfig = async () => {
  const closed = { accepting: false, privacyUrl: null };
  try {
    const response = await request('/config');
    if (!response.ok) return closed;
    const body = await response.json();
    const privacyUrl = safePrivacyUrl(body?.privacy_url);
    return body?.accepting_requests === true && privacyUrl ? { accepting: true, privacyUrl } : closed;
  } catch {
    return closed;
  }
};

// Outcome kinds: 'received' | 'unavailable' | 'invalid' (with fields) | 'conflict' | 'error' (unknown receipt: retry).
export const submitDemoRequest = async (values, idempotencyKey) => {
  let response;
  try {
    response = await request('', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(values),
    });
  } catch {
    return { kind: 'error' };
  }
  if (response.status === 201) {
    try {
      const body = await response.json();
      return body?.received === true ? { kind: 'received' } : { kind: 'error' };
    } catch {
      return { kind: 'error' };
    }
  }
  if (response.status === 503) return { kind: 'unavailable' };
  if (response.status === 409) return { kind: 'conflict' };
  if (response.status === 422) {
    let fields = [];
    try {
      fields = (await response.json())?.fields || [];
    } catch {
      fields = [];
    }
    return { kind: 'invalid', fields };
  }
  return { kind: 'error' };
};
